import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dexie, Table } from 'dexie';
import moment from 'moment';
import { from, switchMap, timer } from 'rxjs';

import { ExpandedState } from '../models';

@Injectable({ providedIn: 'root' })
export class IndexedDBService extends Dexie {
  private destroyRef = inject(DestroyRef);

  private expandedMeetings: Table<ExpandedState>;
  private collapsedMeetings: Table<ExpandedState>;

  constructor() {
    super('localDb');

    this.version(1).stores({
      expandedMeetings: 'id, expiresAt',
      collapsedMeetings: 'id, expiresAt'
    });
    this.expandedMeetings = this.table('expandedMeetings');
    this.collapsedMeetings = this.table('collapsedMeetings');

    this.startCleanup();
  }

  startCleanup(): void {
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;

    timer(0, twentyFourHoursInMs)
      .pipe(
        switchMap(() => from(this.deleteExpired())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  fetchExpandedMeetings = async (meetingId: string): Promise<ExpandedState | null> => {
    return this.expandedMeetings.get(meetingId);
  };

  saveExpandedMeetings = async (meetingId: string, meetingItemIds: string[]): Promise<ExpandedState> => {
    const draft: ExpandedState = { id: meetingId, meetingItemIds, expiredAt: moment().add(14, 'day').toDate() };
    return this.expandedMeetings.put(draft);
  };

  clearExpandedMeetings = async (meetingId: string): Promise<void> => {
    await this.expandedMeetings.delete(meetingId);
  };

  fetchCollapsedMeetings = async (meetingId: string): Promise<ExpandedState | null> => {
    return this.collapsedMeetings.get(meetingId);
  };

  saveCollapsedMeetings = async (meetingId: string, meetingItemIds: string[]): Promise<ExpandedState> => {
    const draft: ExpandedState = { id: meetingId, meetingItemIds, expiredAt: moment().add(14, 'day').toDate() };
    return this.collapsedMeetings.put(draft);
  };

  clearCollapsedMeetings = async (meetingId: string): Promise<void> => {
    await this.collapsedMeetings.delete(meetingId);
  };

  deleteExpired = async (): Promise<void> => {
    const now = new Date();

    const expiredExpandedMeetingKeys = await this.expandedMeetings.where('expiresAt').below(now).primaryKeys();
    const expiredCollapsedMeetingKeys = await this.collapsedMeetings.where('expiresAt').below(now).primaryKeys();

    const deletePromises: Promise<void>[] = [];

    if (expiredExpandedMeetingKeys.length > 0) {
      deletePromises.push(this.expandedMeetings.bulkDelete(expiredExpandedMeetingKeys));
    }
    if (expiredCollapsedMeetingKeys.length > 0) {
      deletePromises.push(this.collapsedMeetings.bulkDelete(expiredCollapsedMeetingKeys));
    }

    await Promise.all(deletePromises);
  };
}
